import React, { useRef, useState } from 'react';
import { useParams } from "react-router-dom";
import { FREE_AND_PREVIEW_PLANS } from '../constants/constants';
import { UPGRADE_DASHBOARD } from '../constants/hotjar-events';
import { Zap, X } from 'lucide-react';
import { useGlobalStore } from '../zustand-store/useGlobalStore';
import { useOrganisation, useOrganisationUsage } from '../http-hooks/organisation/useOrganisation';

const OrganisationUsage = () => {

    const { org_id } = useParams();
    
    const wordCreditsRef = useRef()
    const [isModalShow, setIsModalShow] = useState(false)
    
    const { data: organisation } = useOrganisation({ org_id });
    const { data: orgUsage } = useOrganisationUsage({ org_id });
    
    const toggleUpgradeModal = useGlobalStore((state) => state.toggleUpgradeModal)

    const compactWords = (n) => {
        if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}m`;
        if (n >= 1_000)     return `${+(n / 1_000).toFixed(1)}k`;
        return n.toString();
    };

    const getRenewalDateString = (timestamp) => {
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ]
        const date = new Date(timestamp);
        return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    }

    if (!organisation || !orgUsage || !orgUsage.subscription_status) return null;

    // Fall back to the legacy words_* fields so the UI works whether or not the API has been
    // deployed with the credits_* rename (avoids a hard crash on the transition).
    const creditsRemaining = orgUsage.credits_remaining ?? orgUsage.words_remaining ?? 0;
    const creditsLimit = orgUsage.plan_credits_limit ?? orgUsage.plan_words_limit ?? 0;

    const isPaid = ![...FREE_AND_PREVIEW_PLANS].includes(organisation.plan_id);
    const pct = creditsLimit > 0
        ? Math.max(0, Math.min(100, Math.round((creditsRemaining / creditsLimit) * 100)))
        : 0;
    const isLow = pct <= 20;

    if (isPaid) {
        return (
            <div className="position-relative d-none d-md-block" ref={wordCreditsRef}>
                {/* Inline trigger — desktop only; the same numbers are shown in the mobile sidebar drawer instead */}
                <div
                    onClick={() => setIsModalShow(!isModalShow)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                >
                    <div style={{ height: 17, width: 1, background: '#E2E8F0', flexShrink: 0 }} />
                    <span style={{
                        fontSize: 11,
                        color: isLow ? 'var(--bs-danger)' : '#94A3B8',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                    }}>
                        {creditsRemaining.toLocaleString()} of {compactWords(creditsLimit)} credits
                    </span>
                </div>

                {/* Popover */}
                {isModalShow && (
                    <>
                        <div
                            style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                            onClick={() => setIsModalShow(false)}
                        />
                        <div
                            className="position-absolute end-0 mt-2 rounded-4 p-3"
                            style={{
                                background: 'rgba(255,255,255,0.97)',
                                backdropFilter: 'blur(20px)',
                                WebkitBackdropFilter: 'blur(20px)',
                                border: '1px solid rgba(var(--bs-primary-rgb), 0.15)',
                                boxShadow: '0 8px 32px rgba(var(--bs-primary-rgb), 0.18)',
                                width: 220,
                                zIndex: 100,
                                top: '100%',
                            }}
                        >
                            <button
                                onClick={() => setIsModalShow(false)}
                                className="btn p-0 position-absolute"
                                style={{ top: 10, right: 12, color: 'var(--bs-secondary-color)', background: 'none', border: 'none' }}
                            >
                                <X size={14} />
                            </button>

                            <p className="mb-1 fw-semibold text-body-emphasis" style={{ fontSize: 12 }}>Credits</p>

                            {/* Progress bar */}
                            <div className="rounded-pill overflow-hidden mb-1" style={{ height: 6, background: 'rgba(var(--bs-primary-rgb),0.12)' }}>
                                <div
                                    className="h-100 rounded-pill"
                                    style={{
                                        width: `${pct}%`,
                                        background: isLow
                                            ? 'linear-gradient(90deg, var(--bs-danger), var(--bs-warning))'
                                            : 'linear-gradient(90deg, var(--bs-primary), var(--bs-secondary))',
                                        transition: 'width 0.4s ease',
                                    }}
                                />
                            </div>

                            <p className="mb-2 text-secondary" style={{ fontSize: 11 }}>
                                <span className="fw-semibold" style={{ color: 'var(--bs-primary)' }}>{creditsRemaining.toLocaleString()}</span>
                                {' '}of {compactWords(creditsLimit)} remaining
                            </p>

                            {orgUsage.renews_on && (
                                <p className="mb-2 text-muted" style={{ fontSize: 11 }}>
                                    {orgUsage.subscription_status === 'in_trial' ? 'Trial expires' : 'Renews'}{' '}
                                    <span style={{ color: 'var(--bs-primary)' }}>{getRenewalDateString(orgUsage.renews_on)}</span>
                                </p>
                            )}

                            <button
                                onClick={() => {
                                    window.hj("event", UPGRADE_DASHBOARD);
                                    toggleUpgradeModal(true, { source: 'usage_widget_button', isManual: true });
                                    setIsModalShow(false);
                                }}
                                className="btn btn-sm w-100 fw-semibold"
                                style={{
                                    background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                                    color: 'white',
                                    borderRadius: 50,
                                    fontSize: 12,
                                    border: 'none',
                                }}
                            >
                                Get More Credits
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Free / preview plan — desktop only; the sidebar's "Unlock all features" card covers this on mobile
    return (
        <button
            onClick={() => {
                window.hj("event", UPGRADE_DASHBOARD);
                toggleUpgradeModal(true, { source: 'usage_widget_link', isManual: true });
            }}
            className="btn d-none d-md-flex align-items-center gap-2"
            style={{
                background: 'linear-gradient(135deg, rgba(var(--bs-primary-rgb),0.1) 0%, rgba(var(--bs-secondary-rgb),0.1) 100%)',
                border: '1px solid rgba(var(--bs-primary-rgb), 0.25)',
                borderRadius: 50,
                padding: '5px 14px',
                fontSize: 12,
                color: 'var(--bs-primary)',
                fontWeight: 500,
                whiteSpace: 'nowrap',
            }}
        >
            <Zap size={13} style={{ flexShrink: 0 }} />
            <span><span className="fw-semibold">{creditsRemaining.toLocaleString()}</span> credits left — Upgrade</span>
        </button>
    );
}

export default OrganisationUsage;
